import { GetCompetitions_Venue_competitions_onlySpecificSales } from '~/Providers/CompetitionsProvider/__queries__';
import {
  GetStaffAndCategories_user_company_categories,
  GetItems_items_results,
} from './__queries__';
import { i18nProps } from '~Types';
import { COMPETITION_TYPE } from '../../../__queries__';

export const renderLimitedCategoriesAndItems = (
  onlySpecificSales: GetCompetitions_Venue_competitions_onlySpecificSales,
  fetchedCategories: GetStaffAndCategories_user_company_categories[],
  fetchedItems: GetItems_items_results[],
  i18n: i18nProps,
) => {
  const { categories, items } = onlySpecificSales;

  const getCategoryNameById = (id: string) => {
    const category = fetchedCategories.find((cat) => cat.id === id);
    return category ? i18n(category.nameLang) : '';
  };

  const getItemNameById = (id: string) => {
    const item = fetchedItems.find((it) => it.id === id);
    return item ? i18n(item.titleLang) : '';
  };

  const formatList = (list: string[]) => {
    if (list.length <= 1) return list.join('');
    return `${list.slice(0, -1).join(', ')} & ${list.slice(-1)}`;
  };

  let displayText = '';

  if (categories.length > 0 && items.length > 0) {
    // Both categories and items exist
    const displayedCategories = categories.slice(0, 3).map(getCategoryNameById);
    const remainingCategories = categories.length - 3;
    const displayedItems = items.slice(0, 3).map(getItemNameById);
    const remainingItems = items.length - 3;

    displayText = `${i18n('Competitions.SpecificSales.LimitedTo')} category ${formatList(
      displayedCategories,
    )}`;
    if (remainingCategories > 0) {
      displayText += ` (+${remainingCategories} others)`;
    }

    displayText += ' and ';

    displayText += `product ${formatList(displayedItems)}`;
    if (remainingItems > 0) {
      displayText += ` (+${remainingItems} others)`;
    }
  } else if (categories.length > 0) {
    // Only categories exist
    const displayedCategories = categories.slice(0, 5).map(getCategoryNameById);
    const remainingCategories = categories.length - 5;

    displayText = `${i18n('Competitions.SpecificSales.LimitedTo')} category ${formatList(
      displayedCategories,
    )}`;
    if (remainingCategories > 0) {
      displayText += ` (+${remainingCategories} ${i18n('Competitions.SpecificSales.Others')})`;
    }
  } else if (items.length > 0) {
    // Only items exist
    const displayedItems = items.slice(0, 5).map(getItemNameById);
    const remainingItems = items.length - 5;

    displayText = `${i18n('Competitions.SpecificSales.Limited.Short')} product ${formatList(
      displayedItems,
    )}`;
    if (remainingItems > 0) {
      displayText += ` (+${remainingItems} others)`;
    }
  }

  // Update singular/plural forms based on count
  if (categories.length === 1) {
    displayText = displayText.replace('category', i18n('Competitions.SpecificSales.Category'));
  } else if (categories.length > 1) {
    displayText = displayText.replace('category', i18n('Competitions.SpecificSales.Categories'));
  }

  if (items.length === 1) {
    displayText = displayText.replace('product', i18n('Competitions.SpecificSales.Product'));
  } else if (items.length > 1) {
    displayText = displayText.replace('product', i18n('Competitions.SpecificSales.Products'));
  }

  return displayText;
};

export const renderAllSpecificItems = (
  onlySpecificItems: string[],
  fetchedItems: GetItems_items_results[],
  i18n: i18nProps,
) => {
  const getItemNameById = (id: string) => {
    const item = fetchedItems.find((it) => it.id === id);
    return item ? i18n(item.titleLang) : '';
  };

  const formatList = (list: string[]) => {
    if (list.length <= 1) return list.join('');
    return `${list.slice(0, -1).join(', ')} & ${list.slice(-1)}`;
  };

  let displayText = '';

  if (onlySpecificItems.length > 0) {
    const displayedItems = onlySpecificItems.map(getItemNameById);

    displayText = `Product ${formatList(displayedItems)}`;
  }

  // Update singular/plural forms based on count
  if (onlySpecificItems.length === 1) {
    displayText = displayText.replace('Product', `${i18n('Competitions.SpecificSales.Product')}:`);
  } else if (onlySpecificItems.length > 1) {
    displayText = displayText.replace(
      'Product',
      `${i18n('Competitions.SpecificSales.Products')}:`,
    );
  }

  return displayText;
};

export const renderAllSpecificCategories = (
  onlySpecificCategories: string[],
  fetchedCategories: GetStaffAndCategories_user_company_categories[],
  i18n: i18nProps,
) => {
  const getCategoryNameById = (id: string) => {
    const category = fetchedCategories.find((cat) => cat.id === id);
    return category ? i18n(category.nameLang) : '';
  };

  const formatList = (list: string[]) => {
    if (list.length <= 1) return list.join('');
    return `${list.slice(0, -1).join(', ')} & ${list.slice(-1)}`;
  };

  let displayText = '';

  if (onlySpecificCategories.length > 0) {
    const displayedCategories = onlySpecificCategories.map(getCategoryNameById);

    displayText = `Category ${formatList(displayedCategories)}`;
  }

  // Update singular/plural forms based on count
  if (onlySpecificCategories.length === 1) {
    displayText = displayText.replace(
      'Category',
      `${i18n('Competitions.SpecificSales.Category')}:`,
    );
  } else if (onlySpecificCategories.length > 1) {
    displayText = displayText.replace(
      'Category',
      `${i18n('Competitions.SpecificSales.Categories')}:`,
    );
  }

  return displayText;
};

export const hasSpecificSales = (
  onlySpecificSales: GetCompetitions_Venue_competitions_onlySpecificSales,
) => {
  return onlySpecificSales.items.length > 0 || onlySpecificSales.categories.length > 0;
};

export const getCompetitionTypeLocale = (competitionType: COMPETITION_TYPE, i18n: i18nProps) => {
  const TYPE_OPTIONS = {
    [COMPETITION_TYPE.SALES_AMOUNT]: i18n('Competitions.Type.SalesAmount'),
    [COMPETITION_TYPE.SALES_COUNT]: i18n('Competitions.Type.SalesCount'),
    [COMPETITION_TYPE.SALES_AVERAGE]: i18n('Competitions.Type.SalesAverage'),
    [COMPETITION_TYPE.TIPS_AMOUNT]: i18n('Competitions.Type.TipsAmount'),
    [COMPETITION_TYPE.TIPS_COUNT]: i18n('Competitions.Type.TipsCount'),
    [COMPETITION_TYPE.TIPS_AVERAGE]: i18n('Competitions.Type.TipsAverage'),
  };

  return i18n(TYPE_OPTIONS[competitionType]);
};

interface TimePassed {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const calculateTimePassed = (startDate: string): TimePassed => {
  const difference = +new Date() - +new Date(startDate);
  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
};

export const formatTimePassed = (startDate: string, i18n: i18nProps): string => {
  const timePassed = calculateTimePassed(startDate);

  const { days, hours, minutes, seconds } = timePassed;

  if (days > 0) {
    return `${days} ${i18n('Competitions.Countdown.DaysAgo', days)}`;
  }
  if (hours > 0) {
    return `${hours} ${i18n('Competitions.Countdown.Hours')} ${i18n(
      'Competitions.Countdown.And',
    )} ${minutes} ${i18n('Competitions.Countdown.Minutes')} ${i18n('Competitions.Countdown.Ago')}`;
  }
  if (minutes > 0) {
    return `${minutes} ${i18n('Competitions.Countdown.Minutes')} ${i18n(
      'Competitions.Countdown.And',
    )} ${seconds} ${i18n('Competitions.Countdown.Seconds')} ${i18n('Competitions.Countdown.Ago')}`;
  }
  if (seconds > 0) {
    return `${seconds} ${i18n('Competitions.Countdown.Seconds')} ${i18n(
      'Competitions.Countdown.Ago',
    )}`;
  }
  return i18n('Competitions.Countdown.JustNow');
};
